/**
 * Catalog-domain seam: packages, prices, add-ons, coupons, categories, and the
 * public pricing-page packages. Wraps the mock consts behind hooks so the
 * mock→real flip is an env change (VITE_USE_MOCKS).
 */
import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS, mockDelay, apiGetList } from './client';
import { packages, pricesByPackage, proPrices, coupons, addons, pricingPagePackages } from '../mocks/pricing';
import type { Price } from '../mocks/pricing';
import { packageCategories } from '../mocks/clientBatch';

export const listPackages = () =>
  USE_MOCKS ? mockDelay(packages) : apiGetList<(typeof packages)[number]>('/packages');
// Flat list (each Price already carries packageId) — the GET /prices shape.
export const getPricesByPackage = () =>
  USE_MOCKS ? mockDelay(Object.values(pricesByPackage).flat()) : apiGetList<Price>('/prices');
export const getProPrices = () =>
  USE_MOCKS ? mockDelay(proPrices) : apiGetList<(typeof proPrices)[number]>('/prices/pro');
export const listCoupons = () =>
  USE_MOCKS ? mockDelay(coupons) : apiGetList<(typeof coupons)[number]>('/coupons');
export const listAddons = () =>
  USE_MOCKS ? mockDelay(addons) : apiGetList<(typeof addons)[number]>('/add-ons');
// Host-aware (Decision #21): pass the customer-facing hostname so a reseller's
// domain returns the reseller's OWN published packages (the API resolves
// host→reseller); platform/direct hosts return the platform catalog. Mirrors
// fetchHostBrand() — the API host differs from window.location.host.
export const listPricingPagePackages = () =>
  USE_MOCKS
    ? mockDelay(pricingPagePackages)
    : apiGetList<(typeof pricingPagePackages)[number]>(
        `/pricing?host=${encodeURIComponent(typeof window !== 'undefined' ? window.location.host : '')}`,
      );
export const listPackageCategories = () =>
  USE_MOCKS ? mockDelay(packageCategories) : apiGetList<(typeof packageCategories)[number]>('/package-categories');

export const usePackages = () => useQuery({ queryKey: ['packages'], queryFn: listPackages });
export const usePricesByPackage = () =>
  useQuery({ queryKey: ['prices', 'byPackage'], queryFn: getPricesByPackage });
export const useProPrices = () => useQuery({ queryKey: ['prices', 'pro'], queryFn: getProPrices });
export const useCoupons = () => useQuery({ queryKey: ['coupons'], queryFn: listCoupons });
export const useAddons = () => useQuery({ queryKey: ['addons'], queryFn: listAddons });
export const usePricingPagePackages = () =>
  useQuery({ queryKey: ['pricing', 'packages'], queryFn: listPricingPagePackages });
export const usePackageCategories = () =>
  useQuery({ queryKey: ['packageCategories'], queryFn: listPackageCategories });
